/**
 * Momentum Dashboard - Phase 8 Deal Momentum Signals.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  Flame,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Activity,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import momentumService from '../services/momentumService';
import { MomentumDashboard, HotDealEntry } from '../types/dealMomentum';
import MomentumBadge from '../components/MomentumBadge';
import VelocityIndicator from '../components/VelocityIndicator';
import MomentumHeatmap from '../components/MomentumHeatmap';

function formatCurrency(n?: number | null): string {
  if (n == null) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatDateTime(s?: string | null): string {
  return s ? new Date(s).toLocaleString() : '—';
}

function hotDealEntityName(d: HotDealEntry): string {
  if (d.entity_name) return d.entity_name;
  if (d.project?.project_name) return d.project.project_name;
  if (d.startup?.name) return d.startup.name;
  if (d.project_id) return `Project #${d.project_id}`;
  if (d.startup_id) return `Startup #${d.startup_id}`;
  return `Deal #${d.id}`;
}

function hotDealLink(d: HotDealEntry): string {
  const kind = d.entity_kind || (d.project_id ? 'project' : 'startup');
  const eid = d.project_id || d.startup_id || 0;
  return `/momentum/${kind}/${eid}`;
}

const MomentumDashboardPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<MomentumDashboard | null>(null);
  const [hotDeals, setHotDeals] = useState<HotDealEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    const [dashRes, hotRes] = await Promise.all([
      momentumService.fetchDashboard(),
      momentumService.fetchHotDeals(20),
    ]);
    if (dashRes.error) {
      toast.error(dashRes.error);
    } else if (dashRes.data) {
      setDashboard(dashRes.data);
    }
    if (hotRes.error) {
      toast.error(hotRes.error);
    } else {
      setHotDeals(hotRes.data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    const res = await momentumService.refreshAll();
    setRefreshing(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`Refreshed ${res.data?.refreshed ?? 0} signal(s)`);
    await load();
  };

  const counts = dashboard?.tier_counts || {
    hot: 0,
    warming: 0,
    steady: 0,
    cooling: 0,
    cold: 0,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
              <Flame className="w-6 h-6 mr-2 text-red-500" />
              Deal Momentum
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Track which deals are heating up. Hot deals signal investor
              urgency.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/momentum/hot-deals"
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Hot deals
            </Link>
            <button
              onClick={handleRefreshAll}
              disabled={refreshing}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Refresh all
            </button>
          </div>
        </div>

        {/* Tier KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <TierKpi label="Hot" value={counts.hot} tier="hot" />
          <TierKpi label="Warming" value={counts.warming} tier="warming" />
          <TierKpi label="Steady" value={counts.steady} tier="steady" />
          <TierKpi label="Cooling" value={counts.cooling} tier="cooling" />
          <TierKpi label="Cold" value={counts.cold} tier="cold" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <MetricCard
            icon={<Activity className="w-5 h-5 text-blue-500" />}
            label="Match requests this week"
            value={dashboard?.total_match_requests_this_week ?? 0}
          />
          <MetricCard
            icon={<DollarSign className="w-5 h-5 text-green-500" />}
            label="Aggregate soft commitments"
            value={formatCurrency(
              dashboard?.aggregate_soft_commitment_total ?? 0
            )}
          />
        </div>

        {/* Tier-distribution heatmap */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Momentum tier distribution
          </h3>
          <MomentumHeatmap
            tierCounts={counts}
            deals={hotDeals.map((d) => ({ ...d, tier: d.momentum_tier }))}
          />
        </div>

        {/* Hot deals grid */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3 inline-flex items-center">
            <Flame className="w-4 h-4 mr-1.5 text-red-500" />
            Hot deals
          </h3>
          {loading ? (
            <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
              Loading...
            </div>
          ) : hotDeals.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500 text-sm">
              No hot deals yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {hotDeals.map((d) => (
                <Link
                  key={d.id}
                  to={hotDealLink(d)}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {hotDealEntityName(d)}
                    </h4>
                    <MomentumBadge tier={d.momentum_tier} size="sm" />
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Velocity</div>
                      <VelocityIndicator velocity={d.velocity_score} size="md" />
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Views (week)</div>
                      <div className="text-sm font-medium text-gray-900">
                        {d.views_this_week}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Round progress</span>
                      <span>{Math.round(d.round_progress_percent)}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 bg-gradient-to-r from-red-400 to-red-600"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, d.round_progress_percent)
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent events */}
        {dashboard?.recent_events && dashboard.recent_events.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Recent events
            </h3>
            <div className="space-y-2">
              {dashboard.recent_events.slice(0, 20).map((ev, idx) => (
                <div
                  key={ev.id ?? idx}
                  className="flex items-center justify-between text-sm border-b border-gray-100 last:border-b-0 py-1.5"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="font-medium text-gray-800 capitalize">
                      {String(ev.event_type).replace(/_/g, ' ')}
                    </span>
                    {ev.project_id && (
                      <span className="text-xs text-gray-500">
                        · project #{ev.project_id}
                      </span>
                    )}
                    {ev.startup_id && (
                      <span className="text-xs text-gray-500">
                        · startup #{ev.startup_id}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatDateTime(ev.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

const TierKpi: React.FC<{ label: string; value: number; tier: string }> = ({
  label,
  value,
  tier,
}) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <MomentumBadge tier={tier} size="sm" showIcon={true} />
    </div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
  </div>
);

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
}> = ({ icon, label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
      {icon}
    </div>
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  </div>
);

export default MomentumDashboardPage;
